import React from "react";
import { Accordion } from "subframe/components/Accordion";
import { TagEscalationStatus } from "subframe/components/TagEscalationStatus";
import { TagScore } from "subframe/components/TagScore";
import { Button } from "subframe/components/Button";
import { ExitSurveyQuestion, getStatusVariant, getStatusText } from "../../../types/exitSurvey";

interface ExitSurveyEmployeeProps {
  status: 'completed' | 'pending' | 'not_started';
  questions: ExitSurveyQuestion[];
  type: 'Employee' | 'HRBP';
  surveyLink?: string;
}

const renderQuestionAnswer = (question: ExitSurveyQuestion) => {
  switch (question.type) {
    case 'single':
      return (
        <TagScore text={question.answer as string} variant="txt" />
      );
    case 'multiple':
      const answers = question.answer as string[];
      return (
        <>
          <div className="flex items-start gap-2">
            {answers.slice(0, 5).map((answer, index) => (
              <TagScore key={index} text={answer} variant="txt" />
            ))}
          </div>
          {answers.length > 5 && (
            <div className="flex items-start gap-2">
              {answers.slice(5).map((answer, index) => (
                <TagScore key={index + 5} text={answer} variant="txt" />
              ))}
            </div>
          )}
        </>
      );
    case 'text':
      return (
        <span className="w-full text-body-16 font-body-16 text-subtext-color">
          &quot;{question.answer}&quot;
        </span>
      );
    default:
      return null;
  }
};

const renderPendingQuestion = (question: ExitSurveyQuestion) => {
  return (
    <div className="flex w-full flex-col items-start gap-2 px-2 py-2">
      <span className="w-full text-body-16 font-body-16 text-default-font">
        {question.question}
      </span>
    </div>
  );
};

export const ExitSurveyEmployee: React.FC<ExitSurveyEmployeeProps> = ({ status, questions, type, surveyLink }) => {
  return (
    <Accordion
      trigger={
        <div className="flex w-full items-center gap-4 rounded-lg border border-solid border-neutral-border px-6 py-3">
          <div className="flex grow shrink-0 basis-0 items-center justify-between">
            <span className="text-body-bold font-body-bold text-default-font">
              Exit survey - {type}
            </span>
            <TagEscalationStatus 
              variant={getStatusVariant(status)} 
              score={getStatusText(status)} 
            />
          </div>
          <Accordion.Chevron />
        </div>
      }
      defaultOpen={false}
    >
      <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-2 border-t border-solid border-neutral-border bg-neutral-50 px-4 py-4">
        <div className="flex w-full items-end justify-between">
          <span className="text-section-header font-section-header text-default-font">
            {type}
          </span>
        </div>
        <div className="flex w-full flex-col items-start gap-1 rounded-md bg-white px-2 py-2 shadow-sm">
          {status === 'completed' ? (
            // Show completed survey with answers
            questions.map((question, index) => (
              <div 
                key={question.id}
                className={`flex w-full flex-col items-start gap-2 px-2 py-2 ${
                  index < questions.length - 1 
                    ? 'border-b border-solid border-neutral-border' 
                    : ''
                }`}
              >
                <span className="w-full text-body-16 font-body-16 text-default-font">
                  {question.question}
                </span>
                {renderQuestionAnswer(question)}
              </div>
            ))
          ) : (
            type === 'Employee' ? (
            // Show pending survey with just questions
            questions.map((question, index) => (
              <div 
                key={question.id}
                className={`flex w-full flex-col items-start gap-2 px-2 py-2 ${
                  index < questions.length - 1 
                    ? 'border-b border-solid border-neutral-border' 
                    : ''
                }`}
              >
                {renderPendingQuestion(question)}
              </div>
            ))
            ) : (
              // Show pending HRBP survey
          <div className="flex w-full flex-col items-start border-b border-solid border-neutral-border px-2 py-2">
            <div className="flex flex-col items-start gap-2">
              <span className="w-112 text-body-16 font-body-16 text-default-font">
                You haven&#39;t completed the survey
              </span>
              {surveyLink && (
                <Button
                  icon="FeatherExternalLink"
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                    window.open(surveyLink, '_blank');
                  }}
                >
                  Survey Link
                </Button>
              )}
            </div>
          </div>
            )
          )}
        </div>
      </div>
    </Accordion>
  );
}; 